import { Helmet } from "react-helmet-async";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { catName } from "../i18n";

function SortIcon({ column, sortBy, sortOrder }) {
  if (sortBy !== column) return <span className="ml-0.5 text-gray-300">⇅</span>;
  return <span className="ml-0.5 text-indigo-500">{sortOrder === "asc" ? "▲" : "▼"}</span>;
}

const API_BASE = "";
const PER_PAGE = 50;

function formatDuration(s) {
  if (!s) return "--:--";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function getAuthHeaders() {
  const token = localStorage.getItem("fm_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function adminFetch(url, options = {}) {
  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...options.headers,
    },
  });
  if (res.status === 403) throw new Error("需要管理员权限");
  if (!res.ok) {
    const text = await res.text().catch(() => "请求失败");
    throw new Error(text);
  }
  return res.json();
}

// ── Page number helper — show more pages ────────────────────────────────────
function paginationRange(totalPages, current) {
  // Show as many page numbers as possible — spread across available space
  const WINDOW = 15;  // pages on each side of current
  const EDGE = 5;     // pages always shown at start/end
  if (totalPages <= EDGE * 2 + WINDOW * 2 + 1)
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  const set = new Set();
  for (let i = 1; i <= EDGE; i++) { set.add(i); set.add(totalPages - i + 1); }
  for (let i = current - WINDOW; i <= current + WINDOW; i++) {
    if (i >= 1 && i <= totalPages) set.add(i);
  }
  const sorted = [...set].sort((a, b) => a - b);
  const pages = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push("...");
    pages.push(sorted[i]);
  }
  return pages;
}

// ── Main component ──────────────────────────────────────────────────────────
export default function AdminPage({ lang }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("tracks");
  // pagination state
  const [page, setPage] = useState(1);
  const [pageData, setPageData] = useState(null); // { tracks, total, total_pages }
  const [deletedTracks, setDeletedTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ── Search with debounce ──────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useRef(null);

  function handleSearchChange(e) {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 300);
  }

  // selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  // operation
  const [operating, setOperating] = useState(new Set());
  const [batchOperating, setBatchOperating] = useState(false);
  const [message, setMessage] = useState(null);
  // preview playback
  const [previewId, setPreviewId] = useState(null);
  const audioRef = useRef(null);

  function showMessage(text, type = "success") {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  }

  // ── Sorting state ─────────────────────────────────────────────────────────
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  function handleSort(column) {
    setSortBy((prev) => {
      if (prev === column) {
        // Toggle order
        setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortOrder("asc");
      return column;
    });
    setPage(1);
  }

  // ── SortIcon defined outside render to satisfy react-hooks ──
  const fetchTracks = useCallback(async (p, search) => {
    try {
      setLoading(true);
      setError(null);
      setSelectedIds(new Set());
      const params = new URLSearchParams();
      params.set("page", p || page);
      params.set("per_page", PER_PAGE);
      const sq = search ?? debouncedSearch;
      if (sq.trim()) params.set("q", sq.trim());
      if (sortBy) params.set("sort", sortBy);
      if (sortOrder) params.set("order", sortOrder);
      const data = await adminFetch(`/api/admin/tracks?${params.toString()}`);
      setPageData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, sortBy, sortOrder]);

  // ── Fetch deleted tracks ──────────────────────────────────────────────────
  const fetchDeleted = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedIds(new Set());
      const data = await adminFetch("/api/admin/tracks/deleted");
      setDeletedTracks(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "tracks") fetchTracks(page, debouncedSearch);
    else fetchDeleted();
  }, [tab, page, debouncedSearch, fetchTracks, fetchDeleted]);

  // ── Selection helpers ────────────────────────────────────────────────────
  const tracks = useMemo(() => pageData?.tracks || [], [pageData?.tracks]);
  const currentIds = useMemo(
    () => new Set(tracks.map((t) => t.id)),
    [tracks]
  );
  const allSelected = tracks.length > 0 && tracks.every((t) => selectedIds.has(t.id));

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of currentIds) next.delete(id);
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of currentIds) next.add(id);
        return next;
      });
    }
  }

  // ── Single delete ─────────────────────────────────────────────────────────
  async function handleDelete(trackId, trackName) {
    if (!confirm(`确定要删除曲目「${trackName || trackId}」吗？`)) return;
    setOperating((prev) => new Set(prev).add(trackId));
    try {
      const res = await adminFetch(`/api/admin/tracks/${trackId}/delete`, {
        method: "POST",
        body: JSON.stringify({ reason: "管理员手动删除" }),
      });
      if (res.ok) {
        showMessage(`已删除: ${trackName || trackId}`, "success");
        // Refetch current page
        fetchTracks(page);
      } else {
        showMessage(res.message || "删除失败", "error");
      }
    } catch (e) {
      showMessage(e.message, "error");
    } finally {
      setOperating((prev) => {
        const next = new Set(prev);
        next.delete(trackId);
        return next;
      });
    }
  }

  // ── Batch delete ──────────────────────────────────────────────────────────
  async function handleBatchDelete() {
    if (selectedIds.size === 0) return;
    if (!confirm(`确定要批量删除 ${selectedIds.size} 首曲目吗？`)) return;
    setBatchOperating(true);
    try {
      const res = await adminFetch("/api/admin/tracks/batch-delete", {
        method: "POST",
        body: JSON.stringify({
          track_ids: [...selectedIds],
          reason: "管理员批量删除",
        }),
      });
      if (res.ok) {
        showMessage(res.message || `已删除 ${selectedIds.size} 首`, "success");
        setSelectedIds(new Set());
        fetchTracks(page);
      } else {
        showMessage(res.message || "批量删除失败", "error");
      }
    } catch (e) {
      showMessage(e.message, "error");
    } finally {
      setBatchOperating(false);
    }
  }

  // ── Single restore ────────────────────────────────────────────────────────
  async function handleRestore(trackId, trackName) {
    if (!confirm(`确定要恢复曲目「${trackName || trackId}」吗？`)) return;
    setOperating((prev) => new Set(prev).add(trackId));
    try {
      const res = await adminFetch(`/api/admin/tracks/${trackId}/restore`, {
        method: "POST",
      });
      if (res.ok) {
        showMessage(`已恢复: ${trackName || trackId}`, "success");
        setDeletedTracks((prev) => prev.filter((d) => d.track_id !== trackId));
      } else {
        showMessage(res.message || "恢复失败", "error");
      }
    } catch (e) {
      showMessage(e.message, "error");
    } finally {
      setOperating((prev) => {
        const next = new Set(prev);
        next.delete(trackId);
        return next;
      });
    }
  }

  // ── Batch restore ─────────────────────────────────────────────────────────
  async function handleBatchRestore() {
    if (selectedIds.size === 0) return;
    if (!confirm(`确定要批量恢复 ${selectedIds.size} 首曲目吗？`)) return;
    setBatchOperating(true);
    try {
      let okCount = 0;
      for (const tid of selectedIds) {
        const res = await adminFetch(`/api/admin/tracks/${tid}/restore`, {
          method: "POST",
        });
        if (res.ok) okCount++;
      }
      showMessage(`已恢复 ${okCount} 首`, "success");
      setSelectedIds(new Set());
      fetchDeleted();
    } catch (e) {
      showMessage(e.message, "error");
    } finally {
      setBatchOperating(false);
    }
  }

  // ── Nav to page ──────────────────────────────────────────────────────────
  function goPage(p) {
    if (p < 1 || p > (pageData?.total_pages || 1)) return;
    setSelectedIds(new Set());
    setPage(p);
  }

  // ── Preview playback ────────────────────────────────────────────────────
  function togglePreview(trackUrl, trackId) {
    if (previewId === trackId) {
      // Stop
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPreviewId(null);
      return;
    }
    // Stop previous if any
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    const audio = new Audio(trackUrl);
    audio.volume = 0.8;
    audio.addEventListener("ended", () => setPreviewId(null));
    audio.addEventListener("error", () => {
      setPreviewId(null);
      showMessage("试听播放失败", "error");
    });
    audio.play().catch(() => {
      setPreviewId(null);
      showMessage("试听播放失败", "error");
    });
    audioRef.current = audio;
    setPreviewId(trackId);
  }

  // ── Display vars ──────────────────────────────────────────────────────────
  const total = pageData?.total || 0;
  const totalPages = pageData?.total_pages || 1;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Admin — FreeMusic.Top</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900">管理面板</h1>
          </div>
          <span className="text-xs text-gray-400">Admin</span>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-2">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => { setTab("tracks"); setPage(1); }}
            className={`px-5 py-2 text-sm font-medium rounded-md transition ${
              tab === "tracks"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            曲目管理 ({total})
          </button>
          <button
            onClick={() => setTab("deleted")}
            className={`px-5 py-2 text-sm font-medium rounded-md transition ${
              tab === "deleted"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            已删除 ({deletedTracks.length})
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        {/* Toast */}
        {message && (
          <div className={
            "mb-4 px-4 py-2.5 rounded-lg text-sm font-medium backdrop-blur-sm shadow-sm " +
            (message.type === "success" ? "bg-green-600/90 text-white" :
             message.type === "error" ? "bg-red-600/90 text-white" :
             "bg-yellow-600/90 text-white")
          }>
            {message.text}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <div className="animate-pulse">加载中...</div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center justify-center py-20 text-red-500 flex-col">
            <div className="text-lg mb-2">⚠️ {error}</div>
            <button
              onClick={() => (tab === "tracks" ? fetchTracks(page) : fetchDeleted())}
              className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition"
            >
              重试
            </button>
          </div>
        )}

        {/* ======== Tab: Tracks ======== */}
        {!loading && !error && tab === "tracks" && (
          <div>
            {/* Search + Batch delete bar */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="搜索所有曲目..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 transition"
                />
              </div>
              {selectedIds.size > 0 && (
                <button
                  onClick={handleBatchDelete}
                  disabled={batchOperating}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                    batchOperating
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-red-500 text-white hover:bg-red-600 shadow-sm"
                  }`}
                >
                  {batchOperating ? "删除中..." : `批量删除 (${selectedIds.size})`}
                </button>
              )}
            </div>

            {/* Info bar */}
            <div className="text-sm text-gray-500 mb-3 flex items-center justify-between">
              <span>
                {debouncedSearch.trim()
                  ? `搜索找到 ${total} 首`
                  : `第 ${page}/${totalPages} 页 · 共 ${total} 首曲目`
                }
                {selectedIds.size > 0 && (
                  <span className="ml-2 text-indigo-600 font-medium">
                    已选 {selectedIds.size} 首
                  </span>
                )}
              </span>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                        />
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">#</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort("name_zh")}>
                        名称 <SortIcon column="name_zh" sortBy={sortBy} sortOrder={sortOrder} />
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700 hidden md:table-cell" onClick={() => handleSort("category")}>
                        分类 <SortIcon column="category" sortBy={sortBy} sortOrder={sortOrder} />
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 hidden sm:table-cell">类型</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700 hidden sm:table-cell" onClick={() => handleSort("duration_s")}>
                        时长 <SortIcon column="duration_s" sortBy={sortBy} sortOrder={sortOrder} />
                      </th>
                      <th className="text-center px-3 py-3 font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700 hidden lg:table-cell" onClick={() => handleSort("like_count")}>
                        ♥ <SortIcon column="like_count" sortBy={sortBy} sortOrder={sortOrder} />
                      </th>
                      <th className="text-center px-3 py-3 font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700 hidden lg:table-cell" onClick={() => handleSort("favorite_count")}>
                        ⭐ <SortIcon column="favorite_count" sortBy={sortBy} sortOrder={sortOrder} />
                      </th>
                      <th className="text-center px-3 py-3 font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700 hidden lg:table-cell" onClick={() => handleSort("dislike_count")}>
                        👎 <SortIcon column="dislike_count" sortBy={sortBy} sortOrder={sortOrder} />
                      </th>
                      <th className="text-center px-4 py-3 font-medium text-gray-500">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tracks.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center py-12 text-gray-400">没有找到曲目</td>
                      </tr>
                    ) : (
                      tracks.map((track, i) => (
                        <tr
                          key={track.id}
                          className={`border-b border-gray-50 transition ${
                            selectedIds.has(track.id) ? "bg-indigo-50/50" : "hover:bg-gray-50/50"
                          }`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(track.id)}
                              onChange={() => toggleSelect(track.id)}
                              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-xs font-mono">
                            {(page - 1) * PER_PAGE + i + 1}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900 truncate max-w-[200px] md:max-w-[300px]">
                              {track.name_zh || track.name_en || track.name}
                            </div>
                            <div className="text-xs text-gray-400 font-mono truncate max-w-[200px]">
                              {track.id}
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                              {catName(lang, track.category, track.category)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">
                            {track.type === "songs" ? "演唱" : "器乐"}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">
                            {formatDuration(track.duration_s)}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-400 text-xs hidden lg:table-cell">
                            {track.like_count || 0}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-400 text-xs hidden lg:table-cell">
                            {track.favorite_count || 0}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-400 text-xs hidden lg:table-cell">
                            {track.dislike_count || 0}
                          </td>
                          <td className="px-4 py-3 text-center flex items-center justify-center gap-1">
                            <button
                              onClick={() => togglePreview(track.url, track.id)}
                              className={`w-7 h-7 flex items-center justify-center rounded-full transition ${
                                previewId === track.id
                                  ? "bg-indigo-600 text-white shadow-sm"
                                  : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                              }`}
                              title={previewId === track.id ? "停止" : "试听"}
                            >
                              {previewId === track.id ? (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                  <rect x="6" y="4" width="4" height="16" rx="1" />
                                  <rect x="14" y="4" width="4" height="16" rx="1" />
                                </svg>
                              ) : (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleDelete(track.id, track.name_zh || track.name_en || track.name)}
                              disabled={operating.has(track.id)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                                operating.has(track.id)
                                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                  : "bg-red-50 text-red-600 hover:bg-red-100"
                              }`}
                            >
                              {operating.has(track.id) ? "删除中..." : "删除"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination — full-width with many visible pages */}
            {pageData && pageData.total_pages > 1 && (
              <div className="flex items-center justify-start gap-1 flex-nowrap mt-5 pb-4 overflow-x-auto">
                <button
                  onClick={() => goPage(page - 1)}
                  disabled={page <= 1}
                  className={`shrink-0 px-2.5 py-1.5 text-xs rounded-md transition ${
                    page <= 1
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  ‹
                </button>
                {paginationRange(totalPages, page).map((p, i) =>
                  p === "..." ? (
                    <span key={`ellipsis-${i}`} className="shrink-0 px-1.5 text-gray-400 select-none text-xs">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => goPage(p)}
                      className={`shrink-0 min-w-[30px] h-7 text-xs rounded font-medium transition ${
                        p === page
                          ? "bg-indigo-600 text-white shadow-sm"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  onClick={() => goPage(page + 1)}
                  disabled={page >= totalPages}
                  className={`shrink-0 px-2.5 py-1.5 text-xs rounded-md transition ${
                    page >= totalPages
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  ›
                </button>
              </div>
            )}
          </div>
        )}

        {/* ======== Tab: Deleted ======== */}
        {!loading && !error && tab === "deleted" && (
          <div>
            {deletedTracks.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <div className="text-5xl mb-4">🗑️</div>
                <p className="text-base">暂无已删除曲目</p>
              </div>
            ) : (
              <div>
                {/* Batch restore bar */}
                {selectedIds.size > 0 && (
                  <div className="mb-3">
                    <button
                      onClick={handleBatchRestore}
                      disabled={batchOperating}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                        batchOperating
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                      }`}
                    >
                      {batchOperating ? "恢复中..." : `批量恢复 (${selectedIds.size})`}
                    </button>
                  </div>
                )}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50">
                          <th className="px-4 py-3 w-10">
                            <input
                              type="checkbox"
                              checked={deletedTracks.length > 0 && deletedTracks.every((d) => selectedIds.has(d.track_id))}
                              onChange={() => {
                                if (deletedTracks.every((d) => selectedIds.has(d.track_id))) {
                                  setSelectedIds(new Set());
                                } else {
                                  setSelectedIds(new Set(deletedTracks.map((d) => d.track_id)));
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                          </th>
                          <th className="text-left px-4 py-3 font-medium text-gray-500">曲目名</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-500 hidden sm:table-cell">分类</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-500">删除时间</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">原因</th>
                          <th className="text-center px-4 py-3 font-medium text-gray-500">操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deletedTracks.map((d) => (
                          <tr
                            key={d.track_id}
                            className={`border-b border-gray-50 transition ${
                              selectedIds.has(d.track_id) ? "bg-emerald-50/50" : "hover:bg-emerald-50/30"
                            }`}
                          >
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(d.track_id)}
                                onChange={() => toggleSelect(d.track_id)}
                                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900 truncate max-w-[200px]">
                                {d.track?.name_zh || d.track?.name_en || d.track?.name || "(无数据)"}
                              </div>
                              <div className="text-xs text-gray-400 font-mono truncate max-w-[200px]">
                                {d.track_id}
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                                {d.track?.category || "—"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs">
                              {d.deleted_at ? d.deleted_at.slice(0, 19).replace("T", " ") : "—"}
                            </td>
                            <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell max-w-[150px] truncate">
                              {d.reason || "—"}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleRestore(d.track_id, d.track?.name_zh || d.track?.name_en || d.track?.name)}
                                disabled={operating.has(d.track_id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                                  operating.has(d.track_id)
                                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                    : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                                }`}
                              >
                                {operating.has(d.track_id) ? "恢复中..." : "恢复"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
